/**
 * Standardized Error Handling Utility
 * Maps backend error codes to user-friendly translated messages.
 */
import i18n from '../i18n';

// Error code to translation key mapping
const ERROR_CODE_MAP = {
  'UNAUTHORIZED': 'errors.unauthorized',
  'FORBIDDEN': 'errors.forbidden',
  'NOT_FOUND': 'errors.notFound',
  'VALIDATION_ERROR': 'errors.validation',
  'BAD_REQUEST': 'errors.badRequest',
  'CONFLICT': 'errors.conflict',
  'SERVER_ERROR': 'errors.serverError',
  'SERVICE_UNAVAILABLE': 'errors.serviceUnavailable',
  'NETWORK_ERROR': 'errors.networkError'
};

/**
 * Get a user-friendly error message from an API error response
 * @param {Error|Object} error - Axios error or error response
 * @returns {string} Translated error message
 */
export function getErrorMessage(error) {
  // Network error (no response)
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return i18n.t('errors.timeout');
    }
    return i18n.t('errors.networkError');
  }

  const { status, data } = error.response;

  // Check for standardized error response
  if (data?.code && ERROR_CODE_MAP[data.code]) {
    const baseMessage = i18n.t(ERROR_CODE_MAP[data.code]);
    
    // For validation errors, include field details
    if (data.code === 'VALIDATION_ERROR' && data.details?.length > 0) {
      const fieldErrors = data.details.map(d => `${d.field}: ${d.message}`).join(', ');
      return `${baseMessage}: ${fieldErrors}`;
    }
    
    // Use server message if more specific
    if (data.message && data.message !== 'Validation failed') {
      return data.message;
    }
    
    return baseMessage;
  }

  // Fallback for legacy error responses
  if (data?.detail) {
    return data.detail;
  }

  // Status code based fallback
  switch (status) {
    case 401:
      return i18n.t('errors.unauthorized');
    case 403:
      return i18n.t('errors.forbidden');
    case 404:
      return i18n.t('errors.notFound');
    case 422:
      return i18n.t('errors.validation');
    case 500:
      return i18n.t('errors.serverError');
    case 503:
      return i18n.t('errors.serviceUnavailable');
    default:
      return i18n.t('errors.generic');
  }
}

/**
 * Check if error indicates no data for filters (empty result, not an error)
 * @param {Error} error - Axios error
 * @returns {boolean}
 */
export function isNoDataError(error) {
  return error.response?.status === 404 && 
         error.response?.data?.code === 'NOT_FOUND';
}

/**
 * Check if error is an authentication error requiring re-login
 * @param {Error} error - Axios error
 * @returns {boolean}
 */
export function isAuthError(error) {
  return error.response?.status === 401;
}

/**
 * Check if error is a permission/authorization error
 * @param {Error} error - Axios error
 * @returns {boolean}
 */
export function isForbiddenError(error) {
  return error.response?.status === 403;
}

/**
 * Format validation errors for display
 * @param {Object} errorResponse - API error response with details array
 * @returns {Object} Field-keyed error messages
 */
export function formatValidationErrors(errorResponse) {
  const errors = {};
  if (errorResponse?.details) {
    for (const detail of errorResponse.details) {
      errors[detail.field] = detail.message;
    }
  }
  return errors;
}
